import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Container } from "react-bootstrap";
import { useNavigate, useNavigationType } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import BulkOfferTableSection from "./BulkOfferTableSection";
import {
    fetchMarketplaceConfig,
    fetchMarketplaceListings,
    fetchMarketplaceStats,
    fetchMarketplaceCategories,
    moderateListing,
    toggleListingActiveStatus
} from "../../../redux/slice/marketplaceSlice";
import { fetchActiveCities, fetchActiveStates } from "../../../redux/slice/locationSlice";
import BulkOfferModal from "./BulkOfferModal";
import {
    PublishedIcon,
    PendingIcon,
    PlusIcon,
    BulkOfferIcon,
    ApprovedIcon,
    RejectIcon,
    CrossIcon
} from "../../../assets/icons";
import CommonButton from "../../../components/commonButton";
import { routes } from "../../../routes/pageRoutes";
import { usePermission } from "../../../hooks/usePermission";
import { useToast } from "../../../context/TostContext";
import { TabCard } from "../../../components/custom-card/Cards";
import { FilterBtn } from "../../../components/commonButton/btns";
import { TableSearch } from "../../../components/form/Fields";

const MODULE_SLUG = "marketplace_bulk_offers";

export default function BulkOfferList() {
    const navigate = useNavigate();
    const navigationType = useNavigationType();
    const dispatch = useDispatch();
    const toast = useToast();

    const headerPortalTarget = document.getElementById("header-portal-target");

    const headerContent = (
        <div className="d-flex align-items-center justify-content-between w-100">
            <h4 className="fw-semibold mb-0 text-primary fs-lg">
                <span className="text-muted fw-normal">Marketplace / </span>Bulk Offer
            </h4>
        </div>
    );



    const [limit, setLimit] = useState(() => {
        if (navigationType === "POP") {
            const savedLimit = sessionStorage.getItem("bulk_offer_limit");
            if (savedLimit) return JSON.parse(savedLimit);
        }
        return { value: 10, label: 10 };
    });

    const [currentPage, setCurrentPage] = useState(() => {
        if (navigationType === "POP") {
            const savedPage = sessionStorage.getItem("bulk_offer_page");
            if (savedPage) return parseInt(savedPage, 10);
        }
        return 1;
    });

    const [cardStatus, setCardStatus] = useState(() => {
        if (navigationType === "POP") {
            return sessionStorage.getItem("bulk_offer_cardStatus") || "";
        }
        return "";
    });

    const [searchTerm, setSearchTerm] = useState(() => {
        return navigationType === "POP" ? sessionStorage.getItem("bulk_offer_searchTerm") || "" : "";
    });

    const [filterModal, setFilterModal] = useState(false);
    const [filterValues, setFilterValues] = useState(() => {
        if (navigationType === "POP") {
            const stored = sessionStorage.getItem("bulk_offer_filterValues");
            console.log("stored", stored);
            if (stored) {
                try {
                    return JSON.parse(stored);
                } catch (e) {
                    console.error("Failed to parse filter values", e);
                }
            }
        }
        return {
            category_id: [],
            seller_type: "company",
            state_id: [],
            city_id: [],
            min_price: "",
            max_price: ""
        };
    });

    const { listings, pagination, stats, categories, config, isLoading, isInitialLoad } = useSelector((state) => state.marketplace);
    const { hasPermission } = usePermission();

    const canCreate = hasPermission(MODULE_SLUG, "create");
    const canUpdate = hasPermission(MODULE_SLUG, "update");

    const MarketplaceStatsCards = [
        {
            label: "All Bulk Offers",
            countKey: "all",
            icon: <BulkOfferIcon />,
            iconClass: "icon-primary",
            status: "",
        },
        {
            label: "Pending Request",
            countKey: "pending",
            icon: <PendingIcon size={24} />,
            iconClass: "icon-warning",
            status: "pending",
        },
        {
            label: "Approved Request",
            countKey: "approved",
            icon: <ApprovedIcon size={24} />,
            iconClass: "icon-success",
            status: "approved",

        },
        {
            label: "Published Request",
            countKey: "published",
            icon: <PublishedIcon size={24} />,
            iconClass: "icon-info",
            status: "published",
        },
        {
            label: "Rejected Request",
            countKey: "rejected",
            icon: <RejectIcon size={24} />,
            iconClass: "icon-danger",
            status: "rejected",
        },
        {
            label: "Closed Request",
            countKey: "closed",
            icon: <PublishedIcon size={24} />,
            iconClass: "icon-secondary",
            status: "closed",
        },
    ];

    const handleCardClick = (value) => {
        setCardStatus(value);
        setCurrentPage(1);
    };

    const isInitialFetchRef = useRef(true);
    const searchTimeout = useRef(null);

    const getListings = useCallback((isDebounced = false) => {
        const params = {
            page: currentPage,
            limit: limit?.value,
            search: searchTerm,
            status: cardStatus,
            seller_type: 'company',
            ...filterValues
        };

        const fetchList = () => {
            dispatch(fetchMarketplaceListings(params));
        };

        if (isDebounced) {
            if (searchTimeout.current) clearTimeout(searchTimeout.current);
            searchTimeout.current = setTimeout(fetchList, 500);
        } else {
            fetchList();
        }

        sessionStorage.setItem("bulk_offer_limit", JSON.stringify(limit));
        sessionStorage.setItem("bulk_offer_page", currentPage.toString());
        sessionStorage.setItem("bulk_offer_cardStatus", cardStatus);
        sessionStorage.setItem("bulk_offer_searchTerm", searchTerm);
        sessionStorage.setItem("bulk_offer_filterValues", JSON.stringify(filterValues));

    }, [dispatch, currentPage, limit, searchTerm, cardStatus, filterValues]);

    useEffect(() => {
        getListings(isInitialFetchRef.current ? false : true);
        isInitialFetchRef.current = false;
    }, [getListings]);

    useEffect(() => {
        dispatch(fetchMarketplaceConfig());
        dispatch(fetchMarketplaceStats({ seller_type: 'company' }));
        dispatch(fetchMarketplaceCategories());
    }, [dispatch]);

    const fetchStatesOptions = useCallback((search, page) => {
        return dispatch(fetchActiveStates({ search, page, limit: 20 })).unwrap();
    }, [dispatch]);

    const fetchCitiesOptions = useCallback((search, page) => {
        return dispatch(fetchActiveCities({
            search,
            page,
            limit: 20,
            state_ids: filterValues.state_id?.length > 0 ? filterValues.state_id.join(',') : undefined
        })).unwrap();
    }, [dispatch, filterValues.state_id]);





    const getActiveFilters = () => {
        const active = [];
        if (filterValues.category_id?.length > 0) active.push({ type: 'category_id', label: `Categories (${filterValues.category_id.length})` });
        if (filterValues.state_id?.length > 0) active.push({ type: 'state_id', label: `States (${filterValues.state_id.length})` });
        if (filterValues.city_id?.length > 0) active.push({ type: 'city_id', label: `Cities (${filterValues.city_id.length})` });

        const isPriceFiltered = (filterValues.min_price !== "" && filterValues.min_price !== config?.price_bounds?.min) ||
            (filterValues.max_price !== "" && filterValues.max_price !== config?.price_bounds?.max);
        if (isPriceFiltered) {
            active.push({
                type: 'price',
                label: `Price: ₹${filterValues.min_price || config?.price_bounds?.min} - ₹${filterValues.max_price || config?.price_bounds?.max}`
            });
        }
        return active;
    };

    const activeFilters = getActiveFilters();

    const handleRemoveFilter = (filterType) => {
        if (filterType === 'price') {
            setFilterValues({
                ...filterValues,
                min_price: config?.price_bounds?.min || "",
                max_price: config?.price_bounds?.max || ""
            });
        } else {
            setFilterValues({ ...filterValues, [filterType]: [] });
        }
        setCurrentPage(1);
    };

    const handleLimitChange = (option) => {
        if (!option) return;
        setLimit(option);
        setCurrentPage(1);
    };

    const [togglingId, setTogglingId] = useState(null);

    const refetch = () => {
        dispatch(fetchMarketplaceStats({ seller_type: 'company' }));
        getListings();
    };

    const handleToggleActive = async (id, is_active) => {
        try {
            setTogglingId(id);
            await dispatch(toggleListingActiveStatus({ id, is_active })).unwrap();
            // dispatch(fetchMarketplaceStats({ seller_type: 'company' })); // Optional: update stats silently if needed
        } catch (err) {
            console.error("Toggle active error :::", err);
        } finally {
            setTogglingId(null);
        }
    };

    const actionList = [
        {
            title: "View",
            handler: (row) => navigate(routes.viewMarketplaceBulkOffer.replace(":id", row.id), { state: { currentPage: currentPage } }),
        },
        {
            title: "Edit",
            handler: (row) => navigate(routes.editMarketplaceBulkOffer.replace(":id", row.id)),
            isVisible: (row) => row?.status?.toLowerCase() !== 'published' && canUpdate,
        },
        {
            title: "Close",
            handler: async (row) => {
                try {
                    await dispatch(moderateListing({ id: row.id, status: 'closed', seller_type: 'company' })).unwrap();
                    refetch();
                    toast.success("Closed successfully");
                } catch (err) {
                    console.error("Close error :::", err);
                }
            },
            isVisible: (row) => row?.status?.toLowerCase() === 'published' && canUpdate,
            className: "text-secondary"
        },
        {
            title: "Publish",
            handler: async (row) => {
                try {
                    await dispatch(moderateListing({ id: row.id, status: 'published', seller_type: 'company' })).unwrap();
                    refetch();
                    toast.success("Published successfully");
                } catch (err) {
                    console.error("Publish error :::", err);
                }
            },
            isVisible: (row) => (row?.status?.toLowerCase() === 'approved' || row?.status?.toLowerCase() === 'closed') && canUpdate,
            className: "text-info"
        }
    ];
    // console.log(getActions(listings?.[0]));

    return (
        <>
            {headerPortalTarget && createPortal(headerContent, headerPortalTarget)}
            <Container fluid className="px-0">
                {/* Stats Cards — same grid layout as NPA Properties */}
                <div className="grid gap-4">
                    {MarketplaceStatsCards.map((card) => (
                        <TabCard
                            key={card.status} // Use status as key, it's unique
                            className="g-col-4 g-col-xxl-3"
                            count={stats?.listing_stats?.[card.countKey] || 0}
                            title={card.label}
                            icon={card.icon}
                            active={cardStatus === card.status}
                            onClick={() => handleCardClick(card.status)}
                            iconClassName={card.iconClass} // Use theme for iconClassName
                        />
                    ))}
                </div>

                <div className="bg-white rounded-2 overflow-hidden common-shadow mt-4">
                    <div className="p-4 d-flex align-items-center justify-content-between border-bottom flex-wrap gap-3">
                        <div className="d-flex align-items-center gap-4">

                            <TableSearch value={searchTerm}
                                placeholder="Search bulk offers..."
                                onChange={(e) => {
                                    setSearchTerm(e.target.value);
                                    setCurrentPage(1);
                                }} />
                            <FilterBtn onClick={() => setFilterModal(true)} active={activeFilters.length > 0} count={activeFilters.length} />

                        </div>
                        <div className="d-flex align-items-center gap-2">
                            {/* <CommonButton
                            title="Export"
                            variant="outline-secondary"
                            icon={<DownloadIcon width={18} height={18} color="#6E6B7B" />}
                            onClick={() => {}}
                        /> */}
                            {canCreate && (
                                <CommonButton
                                    title="Create Bulk Offer"
                                    variant="primary"
                                    icon={<PlusIcon size={20} />}
                                    onClick={() => navigate(routes.createMarketplaceBulkOffer)}
                                />
                            )}
                        </div>
                    </div>

                    {/* Filter Chips */}
                    {activeFilters.length > 0 && (
                        <div className="px-4 py-2 d-flex flex-wrap align-items-center gap-2 border-bottom" style={{ backgroundColor: '#fdfdfd' }}>
                            <span className="text-secondary fs-sm fw-medium me-1">Applied Filters:</span>
                            {activeFilters.map((filter, index) => (
                                <div key={index}
                                    className="d-flex align-items-center gap-2 px-2 py-1 rounded-2"
                                    style={{
                                        backgroundColor: 'rgba(124, 36, 57, 0.05)',
                                        border: '1px solid rgba(124, 36, 57, 0.2)'
                                    }}
                                >
                                    <span className="fs-xs fw-medium" style={{ color: '#7C2439' }}>{filter.label}</span>
                                    <button
                                        className="border-0 bg-transparent primary p-0 d-flex align-items-center justify-content-center hover-opacity-75"
                                        onClick={() => handleRemoveFilter(filter.type)}

                                    >
                                        <CrossIcon size={12} />
                                    </button>
                                </div>
                            ))}
                            <button
                                className="btn btn-link btn-sm text-decoration-none p-0 ms-2 text-primary fw-semibold"
                                onClick={() => {
                                    setFilterValues({
                                        category_id: [],
                                        seller_type: "company",
                                        state_id: [],
                                        city_id: [],
                                        min_price: config?.price_bounds?.min || "",
                                        max_price: config?.price_bounds?.max || ""
                                    });
                                    setCurrentPage(1);
                                }}
                                style={{ fontSize: '13px' }}
                            >
                                Clear All
                            </button>
                        </div>
                    )}

                    <BulkOfferTableSection
                        data={listings}
                        isLoading={isLoading}
                        isInitialLoad={isInitialLoad}
                        currentPage={currentPage}
                        setCurrentPage={setCurrentPage}
                        pageSize={limit.value}
                        setPageSize={handleLimitChange}
                        totalItems={pagination?.total}
                        onToggleActive={handleToggleActive}
                        togglingId={togglingId}
                        canUpdate={canUpdate}
                        actionList={actionList}
                    />
                </div>

                <BulkOfferModal
                    isOpen={filterModal}
                    onClose={() => setFilterModal(false)}
                    title="Advanced Filters"
                    size="lg"
                    className="common-modal"
                    categories={categories}
                    config={config}
                    filterValues={filterValues}
                    setFilterValues={setFilterValues}
                    fetchStatesOptions={fetchStatesOptions}
                    fetchCitiesOptions={fetchCitiesOptions}
                    onApply={() => {
                        setCurrentPage(1);
                    }}
                    onClear={() => {
                        setFilterValues({
                            category_id: [],
                            seller_type: "company",
                            state_id: [],
                            city_id: [],
                            min_price: config?.price_bounds?.min || "",
                            max_price: config?.price_bounds?.max || ""
                        });
                        setCurrentPage(1);
                    }}
                />
            </Container>
        </>
    );
}
